import { useEffect, useState, useRef } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
  message,
  Select,
  DatePicker,
} from "antd";
import PropTypes from "prop-types";
import { LoadingOutlined } from "@ant-design/icons";
import SimpleReactValidator from "simple-react-validator";
import { translate } from "../../locale";
import AbTestServices from "../../services/AbTestServices";
import moment from "moment";
import "moment/locale/es";
import { getDatePickerLocale } from "../../helpers/DatePickerHelper";

moment.locale("es");

const SimpleReactValidatorMessage = (message) => (
  <div className="text-red-600 pt-2 text-xs">{message}</div>
);

const AbTestFormModal = ({ visible, onComplete, onCancel, editData }) => {
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(false);
  const [values, setValues] = useState({
    type: "",
    name: "",
    start_date: "",
    end_date: "",
  });
  const types = [
    { value: "feedback_peso", label: translate("Feedback de peso") },
    {
      value: "configurable_notification",
      label: translate("Notificación configurable"),
    },
  ];
  const [error, setError] = useState("");
  const [, forceUpdate] = useState();
  const [inEditMode, setInEditMode] = useState(false);

  const simpleValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: SimpleReactValidatorMessage,
      messages: {
        required: translate("Campo requerido"),
        url: translate("URL inválida"),
      },
    })
  );

  useEffect(() => {
    if (editData && Object.keys(editData).length > 0) {
      setInEditMode(true);
      setValues({
        type: editData.type,
        name: editData.name,
        start_date: editData.start_date,
        end_date: editData.end_date,
      });
    }
  }, [editData]);

  useEffect(() => {
    if (visible !== null) {
      setActive(visible);
    }
  }, [visible]);

  const disabled = () => {
    return !simpleValidator.current.allValid();
  };

  const onFinish = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError("Todos los datos son requeridos");
      return;
    }
    if (inEditMode) {
      update();
    } else {
      save();
    }
  };

  const update = async () => {
    try {
      const body = {
        abtest_id: editData.id,
        name: values.name,
        type: values.type,
        start_date: values.start_date,
        end_date: values.end_date,
      };
      const res = await AbTestServices.edit(body);
      if (res) {
        if (res.status >= 200 && res.status < 300) {
          message.success(
            translate("El {0} se actualizó exitosamente.", translate("AB test"))
          );
          resetForm();
          if (onComplete) {
            onComplete(res.data);
          }
        } else if (res.data.detail === "start_date must be before end_date") {
          setError("La Fecha inicio debe ser anterior a la Fecha fin", 6);
        } else {
          setError(res.data.detail, 6);
        }
      } else {
        setError(
          translate("El {0} no pudo ser actualizado.", translate("AB test"))
        );
      }
    } catch (e) {
      console.error("update error: ", e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    try {
      delete values.abtest_id;
      const res = await AbTestServices.add(values);
      if (res) {
        if (res.status >= 200 && res.status < 300) {
          message.success(
            translate("El {0} se guardó exitosamente.", translate("AB test"))
          );
          resetForm();
          if (onComplete) {
            onComplete(res.data);
          }
        } else if (res.data.message === "start_date must be before end_date") {
          setError("La Fecha inicio debe ser anterior a la Fecha fin", 6);
        } else {
          setError(res.data.message, 6);
        }
      } else {
        setError(
          translate("El {0} no pudo ser guardado.", translate("AB test"))
        );
      }
    } catch (e) {
      console.error("save error: ", e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setInEditMode(false);
    setValues({
      abtest_id: "",
      type: "",
      name: "",
      start_date: "",
      end_date: "",
    });
    setError("");
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    simpleValidator.current.purgeFields();
    forceUpdate();
  };

  const handleChange = (name, value) => {
    simpleValidator.current.purgeFields();
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleCancel = () => {
    resetForm();
    setActive(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleDateIntervalChange = (date, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [value]: moment(date).format("YYYY-MM-DD"),
    }));
  };

  return (
    <Modal
      open={active}
      title={translate("Agregar AB Test")}
      onOk={null}
      onCancel={handleCancel}
      footer={false}
    >
      {error && (
        <Alert
          message={error}
          type="error"
          className="mb-6"
          closable
          onClose={() => setError("")}
        />
      )}
      <Form
        initialValues={{ remember: true }}
        onFinish={onFinish}
        autoComplete="off"
        className="w-full"
      >
        <Form.Item>
          <Input
            placeholder="Nombre"
            className="border-custom_primary"
            name="name"
            onChange={(e) => handleChange("name", e.target.value)}
            onBlur={handleBlur}
            value={values.name}
          />
          {simpleValidator.current.message("name", values.name, "required")}
        </Form.Item>
        <Form.Item>
          <Select
            name="type"
            onChange={(value) => {
              setValues((v) => ({ ...v, type: value }));
            }}
            value={values.type}
          >
            <Select.Option value="">
              {translate("Seleccione tipo")}
            </Select.Option>
            {types.map((item) => (
              <Select.Option key={item.value} value={item.value}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
          {simpleValidator.current.message("type", values.type, "required")}
        </Form.Item>
        <Form.Item>
          <DatePicker
            onChange={(date) => handleDateIntervalChange(date, "start_date")}
            format={"DD/MM/YYYY"}
            value={values.start_date ? moment(values.start_date) : null}
            allowClear={false}
            locale={getDatePickerLocale(translate("Fecha inicio"))}
          />
          {simpleValidator.current.message(
            "start_date",
            values.start_date,
            "required"
          )}
        </Form.Item>
        <Form.Item>
          <DatePicker
            onChange={(date) => handleDateIntervalChange(date, "end_date")}
            format={"DD/MM/YYYY"}
            value={values.end_date ? moment(values.end_date) : null}
            allowClear={false}
            locale={getDatePickerLocale(translate("Fecha fin"))}
            disabledDate={(current) => {
              if (!values.start_date) return false;
              return current?.isSameOrBefore(moment(values.start_date), "day");
            }}
          />
          {simpleValidator.current.message(
            "end_date",
            values.end_date,
            "required"
          )}
        </Form.Item>
        <Form.Item>
          <Row align="end" gutter={16}>
            <Col>
              <Button key="back" onClick={handleCancel}>
                {translate("Cancelar")}
              </Button>
            </Col>
            {inEditMode ? (
              <Col>
                <Button
                  disabled={disabled()}
                  htmlType="submit"
                  type="primary"
                  className="bg-custom_primary"
                >
                  {loading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ color: "#666666" }} />
                      }
                    />
                  ) : (
                    "Editar"
                  )}
                </Button>
              </Col>
            ) : (
              <Col>
                <Button
                  disabled={disabled()}
                  htmlType="submit"
                  type="primary"
                  className="bg-custom_primary"
                >
                  {loading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ color: "#666666" }} />
                      }
                    />
                  ) : (
                    "Agregar"
                  )}
                </Button>
              </Col>
            )}
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

AbTestFormModal.propTypes = {
  visible: PropTypes.bool,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
  editData: PropTypes.object,
};

export default AbTestFormModal;
